/**
 * Complete Registration Page
 *
 * Handles registration completion from approved invitation tokens.
 * Users arrive here after clicking the link in their approval email.
 * Creates tenant and admin user from pre-approved registration request.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { completeRegistrationSchema, type CompleteRegistrationValidation } from '../../../lib/validators/registration-request';
import { registrationRequestService } from '../../../api/registration-request.service';
import type { TokenValidationResponse } from '../../../types/registration-request.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import {
  CheckCircleIcon,
  XCircleIcon,
  BuildingOffice2Icon,
  ClockIcon,
} from '@heroicons/react/24/outline';

export const CompleteRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenData, setTokenData] = useState<TokenValidationResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CompleteRegistrationValidation>({
    resolver: zodResolver(completeRegistrationSchema),
    defaultValues: {
      tenantCode: '',
      tenantName: '',
      firstName: '',
      lastName: '',
      password: '',
      country: '',
      city: '',
      address: '',
      phoneNumber: '',
    },
  });

  // Auto-generate tenant code from tenant name
  const handleTenantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue('tenantName', name, { shouldValidate: true });

    // Auto-generate code from name: lowercase, spaces to hyphens, remove invalid chars
    const generatedCode = name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
    setValue('tenantCode', generatedCode, { shouldValidate: true });
  };

  // Auto-format tenant code: lowercase + remove invalid chars
  const handleTenantCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '');
    setValue('tenantCode', formatted, { shouldValidate: true });
  };

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('No invitation token provided. Please check your email for the correct link.');
        return;
      }

      try {
        const data = await registrationRequestService.validateToken(token);
        if (!data.valid) {
          setStatus('error');
          setErrorMessage('This invitation link is invalid or has been used. Please contact support if you need assistance.');
          return;
        }

        setTokenData(data);

        // Pre-fill tenant name from company name
        if (data.companyName) {
          setValue('tenantName', data.companyName);
          // Generate a suggested tenant code from company name
          const suggestedCode = data.companyName
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 30);
          setValue('tenantCode', suggestedCode);
        }

        // Pre-fill contact name
        if (data.contactName) {
          const nameParts = data.contactName.split(' ');
          if (nameParts.length >= 2) {
            setValue('firstName', nameParts[0]);
            setValue('lastName', nameParts.slice(1).join(' '));
          } else {
            setValue('firstName', data.contactName);
          }
        }

        setStatus('form');
      } catch (error) {
        setStatus('error');
        const message = getErrorMessage(error);
        if (message.toLowerCase().includes('expired')) {
          setErrorMessage('This invitation link has expired. Please contact support to request a new invitation.');
        } else if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('invalid')) {
          setErrorMessage('This invitation link is invalid. Please check your email for the correct link.');
        } else {
          setErrorMessage('Unable to validate invitation. Please try again or contact support.');
        }
      }
    };

    validateToken();
  }, [token, setValue]);

  const onSubmit = async (data: CompleteRegistrationValidation) => {
    if (!token) {
      setErrorMessage('No invitation token. Please use the link from your email.');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await registrationRequestService.completeRegistration(token, data);
      setStatus('success');
    } catch (error) {
      const message = getErrorMessage(error);
      if (message.toLowerCase().includes('tenant code') && message.toLowerCase().includes('exists')) {
        setErrorMessage('This tenant code is already taken. Please choose a different one.');
      } else if (message.toLowerCase().includes('expired')) {
        setErrorMessage('This invitation has expired. Please contact support for a new invitation.');
      } else {
        setErrorMessage(message || 'Failed to complete registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Registration Complete!
            </h2>
            <div className="mt-4 space-y-4">
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Your organization has been set up successfully.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-800">
                <p className="font-medium mb-2">You're all set!</p>
                <p>You can now sign in to your Ketrics dashboard and start building.</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
              size="lg"
            >
              Sign In to Your Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
              <XCircleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Invalid Invitation
            </h2>
            <div className="mt-4">
              <Alert variant="error">{errorMessage}</Alert>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Button
              variant="secondary"
              as={Link}
              to="/request-access"
              fullWidth
            >
              Request New Access
            </Button>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
            <BuildingOffice2Icon className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Complete Your Registration
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Welcome, <strong>{tokenData?.contactName}</strong>! Set up your organization below.
          </p>
          {tokenData?.expiresAt && (
            <div className="mt-2 flex items-center justify-center gap-1 text-sm text-amber-600">
              <ClockIcon className="h-4 w-4" />
              <span>
                Invitation expires {new Date(tokenData.expiresAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && (
            <Alert variant="error" dismissible onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          {/* Organization Section */}
          <div className="card shadow-card rounded-lg p-6 space-y-6">
            <h3 className="text-lg font-medium border-b pb-2">
              Organization Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tenant Name */}
              <FormField
                label="Organization Name"
                htmlFor="tenantName"
                error={errors.tenantName?.message}
                required
              >
                <Input
                  id="tenantName"
                  type="text"
                  placeholder="Acme Corporation"
                  error={!!errors.tenantName}
                  value={watch('tenantName')}
                  onChange={handleTenantNameChange}
                />
              </FormField>

              {/* Tenant Code */}
              <FormField
                label="Organization Code"
                htmlFor="tenantCode"
                error={errors.tenantCode?.message}
                helperText="Auto-generated from name. Lowercase letters, numbers, and hyphens only."
                required
              >
                <Input
                  id="tenantCode"
                  type="text"
                  placeholder="acme-corp"
                  error={!!errors.tenantCode}
                  value={watch('tenantCode')}
                  onChange={handleTenantCodeChange}
                />
              </FormField>

              {/* Country */}
              <FormField
                label="Country"
                htmlFor="country"
                error={errors.country?.message}
              >
                <Input
                  id="country"
                  type="text"
                  placeholder="United States"
                  error={!!errors.country}
                  {...register('country')}
                />
              </FormField>

              {/* City */}
              <FormField
                label="City"
                htmlFor="city"
                error={errors.city?.message}
              >
                <Input
                  id="city"
                  type="text"
                  placeholder="San Francisco"
                  error={!!errors.city}
                  {...register('city')}
                />
              </FormField>

              {/* Address */}
              <FormField
                label="Address"
                htmlFor="address"
                error={errors.address?.message}
                required
              >
                <Input
                  id="address"
                  type="text"
                  placeholder="123 Main Street"
                  error={!!errors.address}
                  {...register('address')}
                />
              </FormField>

              {/* Phone Number */}
              <FormField
                label="Phone Number"
                htmlFor="phoneNumber"
                error={errors.phoneNumber?.message}
                helperText="Format: +1 (555) 123-4567"
                required
              >
                <Input
                  id="phoneNumber"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  error={!!errors.phoneNumber}
                  {...register('phoneNumber')}
                />
              </FormField>
            </div>
          </div>

          {/* Admin Account Section */}
          <div className="card shadow-card rounded-lg p-6 space-y-6">
            <h3 className="text-lg font-medium border-b pb-2">
              Admin Account
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <FormField
                label="First Name"
                htmlFor="firstName"
                error={errors.firstName?.message}
                required
              >
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  error={!!errors.firstName}
                  {...register('firstName')}
                />
              </FormField>

              {/* Last Name */}
              <FormField
                label="Last Name"
                htmlFor="lastName"
                error={errors.lastName?.message}
                required
              >
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  error={!!errors.lastName}
                  {...register('lastName')}
                />
              </FormField>

              {/* Email - Display Only */}
              <FormField
                label="Email Address"
                htmlFor="email"
                helperText="This will be your login email"
              >
                <Input
                  id="email"
                  type="email"
                  value={tokenData?.contactEmail || ''}
                  disabled
                  className="bg-gray-50"
                />
              </FormField>

              {/* Password */}
              <FormField
                label="Password"
                htmlFor="password"
                error={errors.password?.message}
                helperText="Min 8 chars with uppercase, lowercase, number, and special character"
                required
              >
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a secure password"
                  error={!!errors.password}
                  {...register('password')}
                />
              </FormField>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Your Account...' : 'Complete Registration'}
          </Button>

          <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
            By completing registration, you agree to our{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
};

export default CompleteRegistrationPage;
